

import {
  Directive,
  EmbeddedViewRef,
  Input,
  OnChanges,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';

import { MwcSafeAny } from 'mwc/core/types';

@Directive({
  selector: '[mwcStringTemplateOutlet]',
  exportAs: 'mwcStringTemplateOutlet'
})
export class MwcStringTemplateOutletDirective<_T = unknown> implements OnChanges {
  private embeddedViewRef: EmbeddedViewRef<MwcSafeAny> | null = null;
  private context = new MwcStringTemplateOutletContext();
  @Input() mwcStringTemplateOutletContext: MwcSafeAny | null = null;
  @Input() mwcStringTemplateOutlet: MwcSafeAny | TemplateRef<MwcSafeAny> = null;

  static ngTemplateContextGuard<T>(
    _dir: MwcStringTemplateOutletDirective<T>,
    _ctx: MwcSafeAny
  ): _ctx is MwcStringTemplateOutletContext {
    return true;
  }

  private recreateView(): void {
    this.viewContainer.clear();
    const isTemplateRef = this.mwcStringTemplateOutlet instanceof TemplateRef;
    const templateRef = (isTemplateRef ? this.mwcStringTemplateOutlet : this.templateRef) as MwcSafeAny;
    this.embeddedViewRef = this.viewContainer.createEmbeddedView(
      templateRef,
      isTemplateRef ? this.mwcStringTemplateOutletContext : this.context
    );
  }

  private updateContext(): void {
    const isTemplateRef = this.mwcStringTemplateOutlet instanceof TemplateRef;
    const newCtx = isTemplateRef ? this.mwcStringTemplateOutletContext : this.context;
    const oldCtx = this.embeddedViewRef!.context as MwcSafeAny;
    if (newCtx) {
      for (const propName of Object.keys(newCtx)) {
        oldCtx[propName] = newCtx[propName];
      }
    }
  }

  constructor(private viewContainer: ViewContainerRef, private templateRef: TemplateRef<MwcSafeAny>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcStringTemplateOutletContext, mwcStringTemplateOutlet } = changes;
    const shouldRecreateView = (): boolean => {
      let shouldOutletRecreate = false;
      if (mwcStringTemplateOutlet) {
        if (mwcStringTemplateOutlet.firstChange) {
          shouldOutletRecreate = true;
        } else {
          const isPreviousOutletTemplate = mwcStringTemplateOutlet.previousValue instanceof TemplateRef;
          const isCurrentOutletTemplate = mwcStringTemplateOutlet.currentValue instanceof TemplateRef;
          shouldOutletRecreate = isPreviousOutletTemplate || isCurrentOutletTemplate;
        }
      }
      const hasContextShapeChanged = (ctxChange: SimpleChange): boolean => {
        const prevCtxKeys = Object.keys(ctxChange.previousValue || {});
        const currCtxKeys = Object.keys(ctxChange.currentValue || {});
        if (prevCtxKeys.length === currCtxKeys.length) {
          for (const propName of currCtxKeys) {
            if (prevCtxKeys.indexOf(propName) === -1) {
              return true;
            }
          }
          return false;
        } else {
          return true;
        }
      };
      const shouldContextRecreate =
        mwcStringTemplateOutletContext && hasContextShapeChanged(mwcStringTemplateOutletContext);
      return shouldContextRecreate || shouldOutletRecreate;
    };

    if (mwcStringTemplateOutlet) {
      this.context.$implicit = mwcStringTemplateOutlet.currentValue;
    }

    const recreateView = shouldRecreateView();
    if (recreateView) {
      /** recreate view when context shape or outlet change **/
      this.recreateView();
    } else {
      /** update context **/
      this.updateContext();
    }
  }
}

export class MwcStringTemplateOutletContext {
  public $implicit: MwcSafeAny;
}
